import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValue, animate, motion } from "framer-motion";
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

function AnimatedCounter({ target, label, prefix = "", suffix = "", duration = 2 }) {
  const [display, setDisplay] = useState(0);
  const motionValue = useMotionValue(0);

  useEffect(() => {
    const controls = animate(motionValue, target, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate(value) {
        setDisplay(Math.floor(value));
      },
    });

    return () => controls.stop();
  }, [motionValue, target, duration]);

  return (
    <div className="text-center">
      <p className="text-4xl text-white font-bold drop-shadow">
        {prefix}{display.toLocaleString()}{suffix}
      </p>
      <p className="text-sm text-white mt-1">{label}</p>
    </div>
  );
}

export default function AboutUsPage() {
  const logos = [
    { src: "meritage-_19_-1-300x300.png", name: "Meritage Homes" },
    { src: "dreamfinders.png", name: "Dream Finders Homes" },
    { src: "drhorton-2-300x300.png", name: "D.R. Horton" },
    { src: "dweekly-_21_-1-300x300.png", name: "David Weekley Homes" },
    { src: "lennar-_9_-300x300.png", name: "Lennar" },
    { src: "truehomes1.png", name: "True Homes" },
    { src: "drb.jpg", name: "DRB Homes" },
    { src: "mihomes.png", name: "M/I Homes" },
    { src: "quinn.png", name: "Quinn Residences" },
    { src: "tripointe.png", name: "Tri Pointe Homes" },
    { src: "redcedar.png", name: "Red Cedar" },
    { src: "century-4-300x300.png", name: "Century Communities" },
    { src: "toll-7-300x300.png", name: "Toll Brothers" },
    { src: "tm-_20_-1-300x300.png", name: "Taylor Morrison" },
    { src: "adams.png", name: "Adams Homes" }
  ];

  const counterSectionRef = useRef(null);
  const isCounterInView = useInView(counterSectionRef, { once: true });

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/navigation-night-v1',
      center: [-81.8397, 32.9396],
      zoom: 5,
      interactive: false,
    });
    return () => map.remove();
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us | CovePoint Development</title>
        <meta name="description" content="Learn about CovePoint's mission, experience, and approach to real estate land development across the Southeast." />
        <meta property="og:title" content="About CovePoint | Land Development Experts" />
        <meta property="og:description" content="Discover CovePoint's mission and track record in real estate development across the Southeast." />
        <meta property="og:image" content="https://covepoint.com/share.jpg" />
        <meta property="og:url" content="https://covepoint.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://covepoint.com/share.jpg" />
        <link rel="canonical" href="https://covepoint.com/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "CovePoint Development Group",
            url: "https://covepoint.com",
            description: "Land development and entitlement company based in Charlotte, NC.",
            sameAs: ["https://www.linkedin.com/company/covepoint"]
          })}
        </script>
      </Helmet>

      <main className="bg-white text-gray-800 overflow-x-hidden">
        {/* Hero */}
        <section className="relative h-[70vh] overflow-hidden">
          <div className="absolute inset-0 z-0 bg-cover bg-center animate-kenburns" style={{ backgroundImage: "url('/liveoak.jpg')" }} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="absolute inset-0 z-10 flex items-center justify-center"
          >
            <div className="bg-black bg-opacity-40 px-6 py-4 rounded shadow-lg">
              <h1 className="text-white text-4xl md:text-6xl font-bold drop-shadow-xl">About CovePoint</h1>
            </div>
          </motion.div>
        </section>

        {/* Who We Are */}
        <section className="py-16 px-6 max-w-5xl mx-auto text-center bg-slate-50">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-8">Who We Are</h2>
            <div className="text-left space-y-6 max-w-3xl mx-auto text-lg text-gray-700 leading-relaxed">
              <p>
                CovePoint Development Group is led by a team with over <strong>60 years of combined experience</strong> in real estate development,
                having entitled and developed more than <strong>20,000 residential lots</strong> throughout the Southeast.
              </p>
              <p>
                Based in Charlotte, NC, CovePoint specializes in <strong>residential, commercial, and multifamily development</strong> across
                high-growth markets. Our team manages the full development lifecycle—from land acquisition and entitlement to infrastructure delivery and strategic disposition.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Success In Numbers */}
        <section ref={counterSectionRef} className="bg-sky-900 text-white py-28 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-semibold mb-12">Success In Numbers</h2>
            {isCounterInView && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                <AnimatedCounter target={60} suffix="+" label="Years Combined Experience" duration={3.5}/>
                <AnimatedCounter target={20000} suffix="+" label="Lots Closed" duration={3.5}/>
                <AnimatedCounter target={97} label="No. of Projects" duration={3.5}/>
                <AnimatedCounter target={492} prefix="$" suffix="M" label="Total Transactions" duration={3.5}/>
              </div>
            )}
          </div>
        </section>

        {/* Our Approach */}
        <section className="bg-white py-16 px-6 max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <div className="grid gap-6 md:grid-cols-2 md:grid-rows-[auto_1fr] md:gap-x-12">
              <h2 className="text-2xl font-bold md:col-span-2">Our Approach</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  CovePoint is a vertically integrated, opportunity-driven firm focused on high-growth markets across the Carolinas and beyond.
                  We acquire, entitle, and develop with clarity, speed, and capital discipline.
                </p>
                <p>
                  Our streamlined process, combined with local expertise and strong relationships, enables us to deliver value to our builder and investment partners—time after time.
                </p>
              </div>
              <div className="text-gray-700 leading-relaxed">
                <p>
                  We leverage real-time data and market knowledge to identify strategic opportunities early.
                  We pride ourselves on transparency, execution, and a track record of profitable land development outcomes—no matter the cycle.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Leadership */}
        <section className="bg-slate-100 py-16 px-6 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Leadership Team</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {/* David Hughes */}
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="space-y-4">
              <h3 className="text-xl font-semibold">David Hughes</h3>
              <p className="text-gray-600 italic">Founding Partner</p>
              <p className="text-gray-700 text-base leading-relaxed">
                With over 25 years of experience in residential land development, David leads CovePoint’s acquisitions, operations, and project dispositions. He brings a sharp eye for identifying land opportunities and a hands-on approach to driving projects through entitlement and development. David’s leadership is grounded in execution, market insight, and building lasting value for partners and communities.
              </p>
            </motion.div>
            {/* Eric Wood */}
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }} className="space-y-4">
              <h3 className="text-xl font-semibold">Eric Wood</h3>
              <p className="text-gray-600 italic">Founding Partner</p>
              <p className="text-gray-700 text-base leading-relaxed">
                Eric has more than 30 years of experience in residential and mixed-use development, having delivered over 20,000 lots and 4,000 homes throughout the Southeast. At CovePoint, he oversees capital strategy—leading investor relations, institutional partnerships, and deal structuring. Eric’s financial expertise and deep industry network are key drivers of the firm’s growth and stability.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Builder Logos */}
        <section className="bg-white py-16 overflow-hidden" aria-label="Builder Partner Logos">
          <h2 className="text-2xl font-bold text-center mb-10 text-sky-800">Our Builder Partners</h2>
          <div className="relative">
            <div className="animate-scroll flex gap-6 md:gap-10 px-6">
              {[...logos, ...logos].map((logo, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 w-25 h-28 md:w-38 md:h-32 rounded border border-gray-300 bg-white flex items-center justify-center transform-gpu transition-transform duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <img
                    src={`/builderlogos/${logo.src}`}
                    alt={`${logo.name} Logo`}
                    className="max-w-[90%] max-h-[90%] object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
          <style>{`
            @keyframes scroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-scroll {
              animation: scroll-left 80s linear infinite;
              display: flex;
              width: max-content;
            }
          `}</style>
        </section>

        {/* Region Map */}
        <section className="relative h-[60vh]">
          <div id="map" className="absolute inset-0 z-0" aria-hidden="true" />
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="backdrop-blur-sm bg-black/40 px-6 py-4 rounded-md shadow-lg">
              <h2 className="text-white text-3xl md:text-4xl font-bold drop-shadow-xl">
                Operating Across The Southeast
              </h2>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
